/**
 * Dashboard view of the British Columbia rules in bcLogic.js (copied from app/incidents/bc.js).
 * Regulatory words stay inside the category suggestion and the export.
 */
import { BcCategory, Incident, IncidentActor, IncidentDocumentation } from "./types";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const bc = require("./bcLogic");

export const PRODUCT_BEHAVIOUR = "Responsive Behaviour / Abuse" as const;
export const BEHAVIOUR_FEATURE = "Responsive Behaviour detection / Abuse Detection" as const;

export interface BcSuggestion {
  category: BcCategory | null;
  regulatory_term: string;
  product_label: string;
  reportable: boolean;
  additional: string[];
  mapping: string;
  needs_nurse_choice: boolean;
}

export interface BcDeadline {
  party: string;
  role: string;
  required: boolean;
  advisory: boolean;
  legal: boolean;
  site_policy: boolean;
  dueAt: string | null;
  grace_minutes?: number;
  display: string;
  basis: string;
  started_at: string;
  legal_due_at?: string | null;
  event_time?: string;
  phone?: string;
}

export interface FilingCard {
  template: "BC_LTC" | "BC_AL" | null;
  submitted_by_neuravue: false;
  health_authority: { name: string; channel: string } | null;
  assisted_living_registry: { form: string; email: string; fax: string; channel: string } | null;
  unverified: string[];
}

export function bcTemplate(incident: Incident): "BC_LTC" | "BC_AL" | null {
  return bc.bcTemplate(incident);
}

export function bcCategorySuggest(
  incident: Incident,
  doc: IncidentDocumentation = incident.documentation
): BcSuggestion {
  return bc.bcCategorySuggest(incident, doc);
}

export function bcDeadlines(incident: Incident, doc: IncidentDocumentation = incident.documentation): BcDeadline[] {
  return bc.bcDeadlines(incident, doc);
}

export function filingCard(incident: Incident): FilingCard {
  return bc.filingCard(incident) as FilingCard;
}

export function validateBc(incident: Incident, now = new Date(), actor: IncidentActor | null = null): string[] {
  return (bc.validateBc as (record: Incident, at: Date, who: IncidentActor | null) => string[])(incident, now, actor);
}

export function notificationComplete(incident: Incident, party: string): boolean {
  return (bc.notificationComplete as (record: Incident, who: string) => boolean)(incident, party);
}

export function bcCategoryChoices(): Array<{ value: string; regulatory: string; product: string }> {
  const categories = bc.CATEGORIES as Record<string, { regulatory: string; product: string }>;
  return Object.entries(categories).map(([value, meta]) => ({
    value,
    regulatory: meta.regulatory,
    product: meta.product
  }));
}

export function categoryAllowed(
  incident: Incident,
  category: string,
  doc: IncidentDocumentation = incident.documentation
): boolean {
  return Boolean(bc.categoryFits(incident, doc, category));
}

export function behaviourLabel(type: Incident["type"]): string {
  if (type === "fall") return "Fall";
  if (type === "unsafe_interaction" || type === "responsive_behaviour") return PRODUCT_BEHAVIOUR;
  if (type === "wandering_zone") return "Wandering or exit-seeking";
  return "Other alert";
}
